import { Link, useCanGoBack, useRouter } from "@tanstack/react-router";
import {
  AlertCircle,
  Bell,
  ClipboardList,
  ExternalLink,
  Trash2,
} from "lucide-react";
import { useEffect, useRef } from "react";

import { AppShell } from "components/AppShell";
import { EmptyState, LoadingRows } from "components/states";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";

import { usePublicEvent } from "../events/use-public-event";

import { LinkifiedText } from "./LinkifiedText";
import { linksIn } from "./linkify";
import { scheduleNotificationDeletion } from "./notification-deletion";
import type { Notification } from "./schemas";
import { markNotificationRead, useNotification } from "./use-notification";
import { APP_LOCALE } from "utils";

/**
 * The page a push click opens: the announcement itself, with its links.
 *
 * A push click lands here rather than on a link inside the message. Opening a
 * third-party URL straight from a system notification skips the app, shows the
 * reader nothing about where they are going, and makes a push a ready-made
 * phishing vector. Here the reader sees the message and the host before they
 * choose to follow it.
 */
export function NotificationDetailPage({
  uid,
  notificationId,
  email,
  signOut,
}: {
  uid: string;
  notificationId: string;
  email: string | null;
  signOut: () => void;
}) {
  const state = useNotification(uid, notificationId);
  const title =
    state.status === "success" && state.data.type !== "ANNOUNCEMENT"
      ? "Notification"
      : "Announcement";

  return (
    <AppShell identity={{ email }} onSignOut={signOut} title={title}>
      <div className="mx-auto max-w-2xl">
        {state.status === "loading" && <LoadingRows rows={3} />}
        {state.status === "not-found" && (
          <EmptyState
            icon={Bell}
            title="Notification not found"
            testId="notification-not-found"
          >
            It may have been removed, or it was sent to a different account.
          </EmptyState>
        )}
        {state.status === "error" && (
          <EmptyState
            icon={AlertCircle}
            title="Something went wrong"
            testId="notification-error"
          >
            {state.error}
          </EmptyState>
        )}
        {state.status === "success" && (
          <NotificationDetail uid={uid} notification={state.data} />
        )}
      </div>
    </AppShell>
  );
}

function NotificationDetail({
  uid,
  notification,
}: {
  uid: string;
  notification: Notification;
}) {
  const links = linksIn(notification.body);
  const router = useRouter();
  const canGoBack = useCanGoBack();

  // Back to wherever the reader came from: the bell's page, usually. A push
  // click opens this page cold, with nothing of ours behind it, and then
  // "back" would leave the app, so it goes home instead.
  const deleteAndLeave = () => {
    scheduleNotificationDeletion(uid, notification.id);
    if (canGoBack) router.history.back();
    else void router.navigate({ to: "/" });
  };

  // Opening the page is reading it. A failed write costs a badge count, not
  // the message, so it is not surfaced. Once per id: the snapshot hands a new
  // object on every change, including the one this write causes, and a write
  // still in flight has not flipped readAt yet.
  const markedRead = useRef<string | null>(null);
  useEffect(() => {
    if (markedRead.current === notification.id) return;
    markedRead.current = notification.id;
    void markNotificationRead(uid, notification).catch(() => undefined);
  }, [uid, notification]);

  return (
    <Card data-testid="notification-detail">
      <CardContent className="space-y-4 pt-6">
        <div>
          {/* JSX text, never HTML: title and body are stored unsanitised. */}
          <h2 className="text-xl font-semibold text-foreground wrap-anywhere">
            {notification.title}
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {notification.createdAt.toDate().toLocaleString(APP_LOCALE)}
          </p>
        </div>
        <p
          className="whitespace-pre-line text-sm text-muted-foreground wrap-anywhere"
          data-testid="notification-detail-body"
        >
          <LinkifiedText text={notification.body} />
        </p>
        {links.length > 0 && (
          <div className="flex flex-col gap-2" data-testid="notification-links">
            {links.map((href) => (
              // The whole host always shows, wrapped if it must: a lookalike
              // such as forms.office.com.survey-login.co differs only at the
              // end, which truncation would hide.
              <Button
                variant="secondary"
                key={href}
                asChild
                className="h-auto min-h-11 justify-start whitespace-normal py-2 text-left dark:border-edge dark:border-lit-edge"
              >
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="notification-link-button"
                >
                  <ExternalLink className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 break-all">
                    Open link on {new URL(href).hostname}
                  </span>
                </a>
              </Button>
            ))}
          </div>
        )}
        {/* One row, so the two actions sit side by side with a gap and wrap
            on a narrow screen. empty:hidden keeps an empty row from adding
            the parent's spacing when neither action applies. */}
        <div
          className="flex flex-wrap gap-2 empty:hidden"
          data-testid="notification-actions"
        >
          {notification.feedbackFormId && (
            // In-app, so it needs none of the host disclosure the external
            // links above carry.
            <Button
              variant="secondary"
              asChild
              className="h-auto min-h-11 justify-start whitespace-normal py-2 text-left dark:border-edge dark:border-lit-edge"
              data-testid="notification-feedback-link"
            >
              <Link
                to="/e/$slug/feedback/$formId"
                params={{
                  slug: notification.eventSlug,
                  formId: notification.feedbackFormId,
                }}
              >
                <ClipboardList className="h-4 w-4 shrink-0" />
                Open feedback form
              </Link>
            </Button>
          )}
          <EventLink slug={notification.eventSlug} />
        </div>
        <div className="border-t pt-4">
          <Button
            type="button"
            variant="destructive"
            onClick={deleteAndLeave}
            data-testid="notification-detail-delete"
            className="max-sm:w-full"
          >
            <Trash2 />
            Delete notification
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Offered only when the event page will actually render. /e/$slug reads the
 * public projection, which exists only while the event is published, and a
 * link to "Event not found" is the confusion this page exists to remove.
 */
function EventLink({ slug }: { slug: string }) {
  const publicEvent = usePublicEvent(slug);
  if (publicEvent.status !== "success") return null;
  return (
    <Button
      asChild
      variant="outline"
      className="h-auto min-h-11 whitespace-normal py-2 text-left wrap-anywhere"
      data-testid="notification-event-link"
    >
      <Link to="/e/$slug" params={{ slug }}>
        Go to {publicEvent.event.title}
      </Link>
    </Button>
  );
}
